import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { CHOIX_YAML_MAX, ecrireStructureJeuSchema, type SceneJeuDetail } from "./contract";
import {
  ALERTE_GRISE,
  ALERTE_PANNE,
  ALERTE_VERTE,
  BOUTON_CONTOUR,
  Carte,
  CHAMP,
  CHAMP_YAML,
  ErreurLivresJeux,
  FilLivresJeux,
  IntrouvableLivresJeux,
  LIEN_BOUTON,
  Rapport,
} from "./livres-jeux-commun";
import { EditeurTexte, useTexteJeu } from "./livres-jeux-editeur";
import {
  apercuDeLaScene,
  messageDEchec,
  RefusJeu,
  useEcrireStructure,
  useSceneJeu,
  useSupprimerScene,
} from "./livres-jeux-requetes";
import { LIBELLE_FIN, penseBete, refusDeCle, titreDeScene } from "./livres-jeux-textes";
import { TextesAvant } from "./livres-jeux-textes-avant";
import { ConsoleShell } from "./shell";
import { affichageDeLecture, Chargement, idValide, RelectureEchouee } from "./states";

/**
 * Une scene (`/livres-jeux/scenes/$id`, spec du 03/10/2026, D4) : son texte, en clair, avec la
 * sauvegarde automatique ; sa structure (identifiant, titre, page imposee, fin, choix en YAML)
 * avec le pense-bete du livre ; l'apercu de sa page ; la precedente et la suivante. Au
 * telephone, le texte d'abord, la structure plus bas.
 */

export function LivresJeuxScenePage({ id }: { id: string }) {
  const valide = idValide(id);
  const lecture = useSceneJeu(id, valide);
  const affichage = affichageDeLecture(lecture);
  const detail = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilLivresJeux livre={detail?.livre}>
        {detail === undefined ? null : titreDeScene(detail.scene)}
      </FilLivresJeux>
      {!valide ? <IntrouvableLivresJeux quoi={`la scène ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="de la scène" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurLivresJeux error={lecture.error} quoi={`la scène ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {/* Une autre scene remonte l'ecran : son texte a son propre moteur de sauvegarde. */}
      {detail === undefined ? null : <Scene detail={detail} key={detail.scene.id} />}
    </ConsoleShell>
  );
}

function Scene({ detail }: { detail: SceneJeuDetail }) {
  const { scene, livre } = detail;
  const cible = { type: "scene" as const, sceneId: scene.id, livreId: livre.id };
  const texte = useTexteJeu(cible, {
    texte: scene.texte,
    version: scene.texte_version,
    provenance: scene.provenance,
  });
  // La version que la structure vient d'ecrire : le formulaire, remonte a chaque version, ne
  // garderait pas son « enregistree ».
  const [structureEcrite, setStructureEcrite] = useState<number | null>(null);
  const ordre = [...livre.scenes].sort((a, b) => a.rang - b.rang);
  const rang = ordre.findIndex((autre) => autre.id === scene.id);
  const precedente = rang > 0 ? ordre[rang - 1] : undefined;
  const suivante = rang >= 0 && rang < ordre.length - 1 ? ordre[rang + 1] : undefined;

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold [overflow-wrap:anywhere]">{titreDeScene(scene)}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono text-xs">{scene.cle}</span>
            {livre.depart === scene.cle ? (
              <span className="rounded-full bg-foreground px-2.5 py-0.5 text-xs font-medium text-background">
                départ
              </span>
            ) : null}
            {scene.fin === null ? null : (
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs">
                {LIBELLE_FIN[scene.fin]}
              </span>
            )}
          </p>
        </div>
        <nav aria-label="Scènes voisines" className="flex flex-wrap gap-2">
          {precedente === undefined ? null : (
            <Link
              className={LIEN_BOUTON}
              params={{ id: String(precedente.id) }}
              to="/livres-jeux/scenes/$id"
            >
              ‹ {titreDeScene(precedente)}
            </Link>
          )}
          {suivante === undefined ? null : (
            <Link
              className={LIEN_BOUTON}
              params={{ id: String(suivante.id) }}
              to="/livres-jeux/scenes/$id"
            >
              {titreDeScene(suivante)} ›
            </Link>
          )}
        </nav>
      </header>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0 md:rounded-lg md:border md:border-border md:bg-card md:p-5">
          <EditeurTexte
            cible={cible}
            invite="Le texte de la scène, tel qu'il sera imprimé : une ligne vide entre deux paragraphes, un simple retour devient un espace. Tout s'enregistre pendant que tu tapes."
            quoi="cette scène"
            texte={texte}
            titre="Texte"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-5">
          <Structure
            detail={detail}
            ecrite={structureEcrite}
            key={`structure-${scene.structure_version}`}
            onEcrite={setStructureEcrite}
          />
          <Apercu sceneId={scene.id} />
          <TextesAvant key={scene.cle} livreId={livre.id} scene={scene.cle} />
          <Supprimer detail={detail} />
        </div>
      </div>
    </>
  );
}

/**
 * La structure : tout sauf le texte, a la version lue. Le moteur lit les choix a
 * l'enregistrement et refuse un YAML illisible ; une cible qui n'existe pas encore passe.
 */
function Structure({
  detail,
  ecrite,
  onEcrite,
}: {
  detail: SceneJeuDetail;
  /** La version que ce formulaire vient d'ecrire, s'il en a ecrit une. */
  ecrite: number | null;
  onEcrite: (version: number) => void;
}) {
  const { scene, livre } = detail;
  const ecrire = useEcrireStructure(scene.id, livre.id);
  const [cle, setCle] = useState(scene.cle);
  const [titre, setTitre] = useState(scene.titre ?? "");
  const [page, setPage] = useState(scene.page === null ? "" : String(scene.page));
  const [fin, setFin] = useState<"" | "reussie" | "ratee">(scene.fin ?? "");
  const [choix, setChoix] = useState(scene.choix_yaml);
  const [refus, setRefus] = useState<string | null>(null);
  const enregistree = ecrite !== null && ecrite === scene.structure_version;
  const idCle = useId();
  const idTitre = useId();
  const idPage = useId();
  const idFin = useId();
  const idChoix = useId();
  const memo = penseBete(livre);
  const erreur = ecrire.error instanceof RefusJeu ? ecrire.error : null;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    // Un double appui n'envoie pas deux fois la meme version (le second ferait un faux 409).
    if (ecrire.isPending) return;
    const refusCle = refusDeCle(cle);
    if (refusCle !== null) {
      setRefus(`Identifiant : ${refusCle}`);
      return;
    }
    const corps = {
      cle,
      titre: titre.trim() === "" ? null : titre.trim(),
      page: page.trim() === "" ? null : Number(page),
      fin: fin === "" ? null : fin,
      choix_yaml: choix,
      version: scene.structure_version,
    };
    const lu = ecrireStructureJeuSchema.safeParse(corps);
    if (!lu.success) {
      setRefus(
        lu.error.issues[0]?.path[0] === "page"
          ? "La page imposée est un entier de 1 à 999, ou rien."
          : "Un champ ne passe pas : vérifie le titre (200 caractères au plus) et les choix.",
      );
      return;
    }
    setRefus(null);
    ecrire.mutate(lu.data, { onSuccess: (reponse) => onEcrite(reponse.scene.structure_version) });
  };

  return (
    <Carte id="livres-jeux-structure" titre="Structure">
      <form className="flex flex-col gap-3" noValidate onSubmit={envoyer}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idCle}>
              Identifiant
            </label>
            <input
              autoCapitalize="none"
              autoCorrect="off"
              className={cn(CHAMP, "font-mono")}
              id={idCle}
              maxLength={60}
              onChange={(evenement) => setCle(evenement.target.value)}
              spellCheck={false}
              value={cle}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idTitre}>
              Titre
            </label>
            <input
              className={CHAMP}
              id={idTitre}
              maxLength={200}
              onChange={(evenement) => setTitre(evenement.target.value)}
              placeholder="sans titre"
              value={titre}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idPage}>
              Page imposée
            </label>
            <input
              className={CHAMP}
              id={idPage}
              inputMode="numeric"
              onChange={(evenement) => setPage(evenement.target.value)}
              placeholder="libre"
              value={page}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idFin}>
              Fin
            </label>
            <select
              className={CHAMP}
              id={idFin}
              onChange={(evenement) => setFin(evenement.target.value as "" | "reussie" | "ratee")}
              value={fin}
            >
              <option value="">pas une fin</option>
              <option value="reussie">fin réussie</option>
              <option value="ratee">fin ratée</option>
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold" htmlFor={idChoix}>
            Choix, en YAML
          </label>
          <textarea
            aria-describedby={`${idChoix}-memo`}
            className={CHAMP_YAML}
            id={idChoix}
            maxLength={CHOIX_YAML_MAX}
            onChange={(evenement) => setChoix(evenement.target.value)}
            placeholder={"- id: monter\n  libelle: Monter au grenier\n  suites:\n    - va: grenier"}
            spellCheck={false}
            value={choix}
          />
          <dl
            className={cn(ALERTE_GRISE, "grid gap-x-3 gap-y-1 text-xs sm:grid-cols-[auto_1fr]")}
            id={`${idChoix}-memo`}
          >
            <dt className="font-semibold">Scènes</dt>
            <dd className="font-mono [overflow-wrap:anywhere]">{memo.scenes}</dd>
            <dt className="font-semibold">Indices</dt>
            <dd className="font-mono [overflow-wrap:anywhere]">{memo.indices}</dd>
            <dt className="font-semibold">Jauges</dt>
            <dd className="font-mono [overflow-wrap:anywhere]">{memo.jauges}</dd>
          </dl>
        </div>
        {refus === null ? null : (
          <p className="text-sm text-destructive" role="alert">
            {refus}
          </p>
        )}
        {erreur !== null && erreur.erreurs.length > 0 ? (
          <div className={ALERTE_PANNE} role="alert">
            <p className="font-semibold">{erreur.message}</p>
            <ul className="mt-1 list-disc pl-5 [overflow-wrap:anywhere]">
              {erreur.erreurs.map((faute) => (
                <li key={faute}>{faute}</li>
              ))}
            </ul>
          </div>
        ) : ecrire.isError ? (
          <p className={ALERTE_PANNE} role="alert">
            {messageDEchec(ecrire.error)}
          </p>
        ) : null}
        {enregistree ? (
          <p className={ALERTE_VERTE} role="status">
            Structure enregistrée.
          </p>
        ) : null}
        <div>
          <Button
            aria-disabled={ecrire.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="submit"
          >
            Enregistrer la structure
          </Button>
        </div>
      </form>
    </Carte>
  );
}

/** La page de la scene, mise en page dans son livre : une image a la demande, jamais gardee. */
function Apercu({ sceneId }: { sceneId: number }) {
  const [etat, setEtat] = useState<
    | { type: "repos" }
    | { type: "calcul" }
    | { type: "image"; adresse: string }
    | { type: "erreur"; erreur: unknown }
  >({ type: "repos" });
  const adresse = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (adresse.current !== null) URL.revokeObjectURL(adresse.current);
    },
    [],
  );

  const voir = async (): Promise<void> => {
    setEtat({ type: "calcul" });
    try {
      const image = await apercuDeLaScene(sceneId);
      if (adresse.current !== null) URL.revokeObjectURL(adresse.current);
      adresse.current = URL.createObjectURL(image);
      setEtat({ type: "image", adresse: adresse.current });
    } catch (erreur) {
      setEtat({ type: "erreur", erreur });
    }
  };

  const refus = etat.type === "erreur" && etat.erreur instanceof RefusJeu ? etat.erreur : null;
  return (
    <Carte id="livres-jeux-apercu" titre="Aperçu de la page">
      <div>
        <button
          className={BOUTON_CONTOUR}
          disabled={etat.type === "calcul"}
          onClick={() => void voir()}
          type="button"
        >
          {etat.type === "calcul"
            ? "Mise en page..."
            : etat.type === "image"
              ? "Rafraîchir"
              : "Voir la page"}
        </button>
      </div>
      {etat.type === "image" ? (
        <img
          alt="La page de la scène, telle qu'elle sera imprimée"
          className="w-full rounded-md border border-border bg-white"
          src={etat.adresse}
        />
      ) : null}
      {refus?.rapport ? <Rapport rapport={refus.rapport} /> : null}
      {etat.type === "erreur" && !refus?.rapport ? (
        <p className={ALERTE_PANNE} role="alert">
          {messageDEchec(etat.erreur)}
        </p>
      ) : null}
    </Carte>
  );
}

/** Supprimer la scene, apres confirmation : son texte reste au journal, sous son identifiant. */
function Supprimer({ detail }: { detail: SceneJeuDetail }) {
  const navigate = useNavigate();
  const supprimer = useSupprimerScene();
  const [confirmation, setConfirmation] = useState(false);
  return (
    <Carte id="livres-jeux-supprimer" titre="Supprimer la scène">
      {confirmation ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            Les scènes suivantes remontent d'un rang, et les choix qui mènent ici n'ont plus de
            cible. Le texte reste au journal du livre : une scène recréée sous l'identifiant «{" "}
            {detail.scene.cle} » le retrouve dans « Textes d'avant ».
          </p>
          <div className="flex flex-wrap gap-2">
            {/* Le `!` garde le contour rouge : BOUTON_CONTOUR pose le contour des controles
                (`border-input`), et Tailwind le classe apres la couleur destructive, qui ne
                gagnerait plus sans lui. */}
            <button
              className={cn(BOUTON_CONTOUR, "border-destructive! text-destructive")}
              disabled={supprimer.isPending}
              onClick={() =>
                supprimer.mutate(detail.scene.id, {
                  onSuccess: () =>
                    void navigate({
                      to: "/livres-jeux/livres/$id",
                      params: { id: String(detail.livre.id) },
                      search: { onglet: "scenes" },
                    }),
                })
              }
              type="button"
            >
              Supprimer {detail.scene.cle}
            </button>
            <button className={BOUTON_CONTOUR} onClick={() => setConfirmation(false)} type="button">
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button className={BOUTON_CONTOUR} onClick={() => setConfirmation(true)} type="button">
            Supprimer…
          </button>
        </div>
      )}
      {supprimer.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {messageDEchec(supprimer.error)}
        </p>
      ) : null}
    </Carte>
  );
}
